/**
 * @module SiteHeader
 * @description Sticky/transparent header behavior: tracks scroll direction to show/hide,
 * manages --header-height CSS custom property, and handles transparent-header overlay logic.
 * @requires @fc/core
 * @requires @fc/component
 * @requires @fc/utilities
 * @customElement site-header
 */
import { calculateHeaderGroupHeight } from '@fc/core';
import { FCElement } from '@fc/component';
import { HEADER_SURFACE_STATE_EVENT } from '@fc/dialog';
import { onDocumentLoaded, changeMetaThemeColor } from '@fc/utilities';
import { HeaderSurfaceCoordinator } from './header-surface-coordinator.js';

class HeaderComponent extends FCElement {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  #drawerW = null;
  #io = null;
  #off = false;
  #lastY = 0;
  #tid = null;
  #animDur = 150;
  #headerSection = null;
  #surfaceCoordinator = null;
  #surfaceGeometryFrame = null;
  #activeSurfaceGeometryTarget = null;
  #scrollFrame = null;

  #activeSurfaceResizeObserver = new ResizeObserver(() => {
    this.#queueInteractiveSurfaceGeometrySync();
  });

  #ro = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height: hostHeight } = entry.target.getBoundingClientRect();
    const row = this.refs.headerRowTop;
    const rowHeight = row instanceof HTMLElement ? row.getBoundingClientRect().height : 0;
    const height = hostHeight > 0 ? hostHeight : rowHeight;

    // Bar mode: header is fixed/not in flow, so --header-height stays 0
    if (this.dataset.headerStyle === 'bar') {
      if (isFinite(height) && height >= 0 && document.body) {
        document.body.style.setProperty('--header-height', '0px');
        document.body.style.setProperty('--bar-header-height', `${height}px`);
        this.style.setProperty('--bar-header-height', `${height}px`);
      }

      this.#queueInteractiveSurfaceGeometrySync();
      this.#surfaceCoordinator?.syncPresentation();
      this.#syncHeaderGroupHeight();
      return;
    }

    // Keep --header-height in sync
    if (isFinite(height) && height >= 0 && document.body) {
      document.body.style.setProperty('--header-height', `${height}px`);
    }

    // Restore menu when viewport widens past the breakpoint
    // eslint-disable-next-line eqeqeq
    if (this.#drawerW != null && isFinite(this.#drawerW) && window.innerWidth > this.#drawerW) {
      this.#setMenuHidden(false);
    }

    this.#surfaceCoordinator?.syncPresentation();
    this.#syncHeaderGroupHeight();
  });

  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#io) return;

    this.#io = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const { isIntersecting } = entry;

        if (alwaysSticky) {
          this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
          const row = this.refs.headerRowTop;
          if (row instanceof HTMLElement) changeMetaThemeColor(row);
        } else {
          this.#off = !isIntersecting || this.dataset.stickyState === 'active';
        }
      },
      { threshold: alwaysSticky ? 1 : 0 },
    );

    this.#io.observe(this);
  };

  #handleOverflowMinimum = (event) => {
    const reached = event?.detail?.minimumReached;
    if (typeof reached === 'boolean') this.#setMenuHidden(reached);
  };

  #setMenuHidden(hide) {
    const drawer = this.refs.headerDrawerContainer;
    const menu = this.refs.headerMenu;
    if (!(drawer instanceof HTMLElement) || !(menu instanceof HTMLElement)) return;

    drawer.classList.toggle('desktop:hidden', !hide);
    menu.classList.toggle('hidden', !!hide);
    this.#drawerW = hide ? (isFinite(window.innerWidth) ? window.innerWidth : null) : null;
  }

  #handleWindowScroll = () => {
    if (this.#scrollFrame !== null) return;

    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = null;
      this.syncAnnouncementOffset();
      if (this.dataset.headerStyle !== 'bar' || this.getAttribute('sticky') !== 'always') {
        this.#updateScrollState();
      }
    });
  };

  #updateScrollState() {
    const stickyMode = this.getAttribute('sticky');

    // Only act when offscreen or in always-sticky mode
    if (!this.#off && stickyMode !== 'always') return;

    let scrollTop = document.scrollingElement?.scrollTop ?? 0;
    if (!isFinite(scrollTop) || scrollTop < 0) scrollTop = 0;

    const scrollingUp = isFinite(this.#lastY) && scrollTop < this.#lastY;

    // Clear any pending animation timeout
    if (this.#tid !== null) {
      clearTimeout(this.#tid);
      this.#tid = null;
    }

    // Always-sticky: just track direction
    if (stickyMode === 'always') {
      const { top } = this.getBoundingClientRect();
      const atTop = isFinite(top) && top >= 0;
      this.dataset.scrollDirection = atTop ? 'none' : scrollingUp ? 'up' : 'down';
      this.#lastY = scrollTop;
      return;
    }

    // Scroll-up sticky mode
    const isBar = this.dataset.headerStyle === 'bar';

    // Bar at top of page: reset to default visible state
    if (isBar && scrollTop <= 1) {
      delete this.dataset.stickyState;
      this.removeAttribute('data-animating');
      this.dataset.scrollDirection = 'none';
      this.#lastY = scrollTop;
      return;
    }

    if (scrollingUp) {
      this.removeAttribute('data-animating');

      if (isBar) {
        // Bar is position: fixed — reveal it
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      } else {
        const { top } = this.getBoundingClientRect();
        const atTop = isFinite(top) && top >= 0;

        if (atTop) {
          // Back at natural position — deactivate
          this.#off = false;
          this.dataset.stickyState = 'inactive';
          this.dataset.scrollDirection = 'none';
        } else {
          // Reveal sticky header
          this.dataset.stickyState = 'active';
          this.dataset.scrollDirection = 'up';
        }
      }
    } else if (this.dataset.stickyState === 'active') {
      // Scrolling down from active — animate out
      this.dataset.scrollDirection = 'none';
      this.setAttribute('data-animating', '');

      this.#tid = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animDur);
    } else {
      // Already idle
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastY = scrollTop;
    if (isBar) {
      this.#queueInteractiveSurfaceGeometrySync();
    }
  }

  #isDesktopBarMode() {
    return this.dataset.headerStyle === 'bar' && window.innerWidth >= 750;
  }

  #queueInteractiveSurfaceGeometrySync() {
    if (!this.#isDesktopBarMode()) return;

    if (this.#surfaceGeometryFrame !== null) {
      cancelAnimationFrame(this.#surfaceGeometryFrame);
    }

    this.#surfaceGeometryFrame = requestAnimationFrame(() => {
      this.#surfaceGeometryFrame = null;
      this.#syncInteractiveSurfaceGeometry();
    });
  }

  #observeActiveSurfaceGeometry(target) {
    if (target === this.#activeSurfaceGeometryTarget) return;

    this.#activeSurfaceResizeObserver.disconnect();
    this.#activeSurfaceGeometryTarget = target instanceof HTMLElement ? target : null;

    if (this.#activeSurfaceGeometryTarget) {
      this.#activeSurfaceResizeObserver.observe(this.#activeSurfaceGeometryTarget);
    }
  }

  #syncInteractiveSurfaceGeometry() {
    if (!this.#isDesktopBarMode()) return;

    const headerRow = this.refs.headerRowTop;
    if (!(headerRow instanceof HTMLElement)) return;

    const rowRect = headerRow.getBoundingClientRect();
    const rowStyles = getComputedStyle(headerRow);
    const viewportHeight = window.innerHeight;

    if (!Number.isFinite(rowRect.bottom) || !Number.isFinite(viewportHeight)) return;

    const headerRect = this.getBoundingClientRect();
    const edgeGap = Number.isFinite(headerRect.top) ? Math.max(headerRect.top, 0) : 0;
    const maxHeight = Math.max(viewportHeight - rowRect.bottom - edgeGap, 0);
    const inlineOverhangStart = Number.parseFloat(rowStyles.borderInlineStartWidth) || 0;
    const inlineOverhangEnd = Number.parseFloat(rowStyles.borderInlineEndWidth) || 0;
    const activePanel = this.querySelector(
      '[data-header-surface][open].fit-header-surface-placement--interactive, ' +
        '[data-header-surface][open] .fit-header-surface-placement--interactive',
    );

    this.#observeActiveSurfaceGeometry(activePanel);

    let activeSurfaceHeight = rowRect.height;
    if (activePanel instanceof HTMLElement) {
      const panelRect = activePanel.getBoundingClientRect();
      if (Number.isFinite(panelRect.bottom) && Number.isFinite(rowRect.top)) {
        activeSurfaceHeight = Math.max(activeSurfaceHeight, panelRect.bottom - rowRect.top);
      }
    }

    this.style.setProperty('--fit-header-surface-max-height', `${maxHeight}px`);
    this.style.setProperty('--fit-header-surface-inline-overhang-start', `${inlineOverhangStart}px`);
    this.style.setProperty('--fit-header-surface-inline-overhang-end', `${inlineOverhangEnd}px`);
    this.style.setProperty('--fit-header-active-surface-height', `${Math.ceil(activeSurfaceHeight)}px`);
  }

  #syncHeaderGroupHeight() {
    this.syncAnnouncementOffset();
    const headerGroup = document.querySelector('#fit-header-group');
    if (!(headerGroup instanceof HTMLElement) || !document.body) return;

    const groupHeight = calculateHeaderGroupHeight(this, headerGroup);
    if (isFinite(groupHeight) && groupHeight >= 0) {
      document.body.style.setProperty('--header-group-height', `${groupHeight}px`);
    }
  }

  syncAnnouncementOffset() {
    let offset = 0;
    if (this.#isDesktopBarMode() && this.hasAttribute('sticky') && this.#headerSection) {
      for (let section = this.#headerSection.previousElementSibling; section; section = section.previousElementSibling) {
        if (section.querySelector('.announcement-bar') && section.getBoundingClientRect().height > 0) {
          offset = Math.max(0, this.#headerSection.getBoundingClientRect().top);
          break;
        }
      }
    }
    const value = `${offset}px`;
    if (this.style.getPropertyValue('--bar-announcement-offset') !== value) {
      this.style.setProperty('--bar-announcement-offset', value);
      this.#queueInteractiveSurfaceGeometrySync();
    }
  }

  connectedCallback() {
    const isBar = this.dataset.headerStyle === 'bar';
    const hasHeaderMenuRef = this.querySelector('[data-ref="headerMenu"]');

    // Some valid header states do not render the horizontal nav menu:
    // - Interactive Island / bar mode
    // - Traditional mode when desktop hamburger drawer is enabled
    // In those cases headerMenu is intentionally absent, so do not require it.
    if (isBar || !(hasHeaderMenuRef instanceof HTMLElement)) {
      this.requiredRefs = this.requiredRefs.filter((r) => r !== 'headerMenu');
    }

    super.connectedCallback();
    this.#headerSection = this.closest('.header-section');
    this.#surfaceCoordinator = new HeaderSurfaceCoordinator({
      host: this,
      getHeaderSection: () => this.#headerSection,
      isDesktopBarMode: () => this.#isDesktopBarMode(),
      requestGeometrySync: () => this.#queueInteractiveSurfaceGeometrySync(),
    });

    this.#ro.observe(this);
    this.addManagedEventListener(document, HEADER_SURFACE_STATE_EVENT, this.#surfaceCoordinator.handleSurfaceState);
    this.#surfaceCoordinator.bootstrap();
    this.#queueInteractiveSurfaceGeometrySync();

    // Overflow menu handler — not used in bar mode
    if (!isBar) {
      this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);
    }

    const sticky = this.getAttribute('sticky');
    if (sticky) {
      if (isBar) {
        // Track preceding announcements as they scroll above the fixed Bar.
        // No IntersectionObserver: fixed elements never leave the viewport.
        document.addEventListener('scroll', this.#handleWindowScroll);
        if (sticky === 'scroll-up') {
          this.#off = true;
        }
      } else {
        this.#observeStickyPosition(sticky === 'always');
        if (sticky === 'scroll-up' || sticky === 'always') {
          document.addEventListener('scroll', this.#handleWindowScroll);
        }
      }
    }
  }

  disconnectedCallback() {
    this.#surfaceCoordinator?.disconnect();
    this.#surfaceCoordinator = null;
    this.#headerSection = null;
    super.disconnectedCallback();

    this.#ro.disconnect();
    this.#activeSurfaceResizeObserver.disconnect();
    this.#activeSurfaceGeometryTarget = null;
    if (this.#surfaceGeometryFrame !== null) {
      cancelAnimationFrame(this.#surfaceGeometryFrame);
      this.#surfaceGeometryFrame = null;
    }

    if (this.#scrollFrame !== null) {
      cancelAnimationFrame(this.#scrollFrame);
      this.#scrollFrame = null;
    }

    if (this.#io) {
      this.#io.disconnect();
      this.#io = null;
    }

    if (this.#tid !== null) {
      clearTimeout(this.#tid);
      this.#tid = null;
    }

    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);

    document.body?.style.setProperty('--header-height', '0px');
  }
}

// Register custom element
if (!customElements.get('site-header')) {
  customElements.define('site-header', HeaderComponent);
}

// Keep header-group height in sync after page load
onDocumentLoaded(() => {
  const header = document.querySelector('#fit-site-header');
  const headerGroup = document.querySelector('#fit-header-group');
  if (!(headerGroup instanceof HTMLElement)) return;

  const observeGroupChildren = (ro) => {
    for (const el of headerGroup.children) {
      if (el !== header && el instanceof HTMLElement) ro.observe(el);
    }
  };

  const ro = new ResizeObserver(() => {
    header?.syncAnnouncementOffset?.();
    const groupHeight = calculateHeaderGroupHeight(header, headerGroup);
    if (document.body && isFinite(groupHeight) && groupHeight >= 0) {
      document.body.style.setProperty('--header-group-height', `${groupHeight}px`);
    }
  });

  observeGroupChildren(ro);

  new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === 'childList') observeGroupChildren(ro);
    }
  }).observe(headerGroup, { childList: true });
});
